import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { setsUpIntegrations } from '@/auth/access'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSetEventGhlLinks } from '@/hooks/useGoHighLevel'
import { ghlLeadsService } from '@/services/GhlLeadsService'
import { GHL_MAX_LINKS_PER_EVENT } from '@lib/constants/gohighlevel'
import type { GhlCoupleDto, GhlPrefillDto } from '@lib/dto/gohighlevel.dto'
import type { EventDetailDto } from '@lib/dto/event.dto'
import { GhlLeadPicker } from './GhlLeadPicker'

const CHECKBOX = 'mt-0.5 h-4 w-4 rounded border-line'

/** What linking could fill in: only what the event is missing and the lead has. */
export interface FillOffer {
  client: { name: string; email: string } | null
  guestCount: number | null
  notes: string | null
}

export function fillOffer(event: EventDetailDto, prefill: GhlPrefillDto): FillOffer {
  return {
    client:
      event.clients.length === 0 && prefill.clientEmail !== null
        ? { name: prefill.clientName ?? prefill.clientEmail, email: prefill.clientEmail }
        : null,
    guestCount: event.guestCount === 0 ? prefill.guestCount : null,
    notes: (event.notes ?? '').trim() === '' ? prefill.notes : null,
  }
}

interface GhlLinkDialogProps {
  event: EventDetailDto
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Link an existing event to the GoHighLevel lead it came from — or to another,
 * replacing the one it has. Offers to fill in what the event is missing from
 * the lead; never what it already has, and never its name.
 */
export function GhlLinkDialog({ event, open, onOpenChange }: GhlLinkDialogProps) {
  const { user } = useAuth()
  const { toast } = useToast()
  const save = useSetEventGhlLinks(event.id, event.venueId)
  const [couple, setCouple] = React.useState<GhlCoupleDto | null>(null)
  const [offer, setOffer] = React.useState<FillOffer | null>(null)
  const [reading, setReading] = React.useState(false)
  const [fill, setFill] = React.useState({ client: true, guestCount: true, notes: true })
  // Each pick is numbered so a slower answer for an earlier one, or one since
  // cleared, can't offer the wrong couple's details.
  const pickSeq = React.useRef(0)

  function reset(): void {
    pickSeq.current += 1
    setReading(false)
    setCouple(null)
    setOffer(null)
    setFill({ client: true, guestCount: true, notes: true })
    save.reset()
  }

  function close(next: boolean): void {
    if (!next) reset()
    onOpenChange(next)
  }

  async function pick(chosen: GhlCoupleDto): Promise<void> {
    pickSeq.current += 1
    const seq = pickSeq.current
    setCouple(chosen)
    setOffer(null)
    const first = chosen.opportunities[0]
    if (first === undefined) {
      setReading(false)
      return
    }
    setReading(true)
    try {
      const prefill = await ghlLeadsService.prefill(event.venueId, {
        connectionId: first.connectionId,
        opportunityId: first.opportunityId,
      })
      if (seq === pickSeq.current) setOffer(fillOffer(event, prefill))
    } catch {
      // Linking still works; there's just nothing to offer to fill in.
      if (seq === pickSeq.current) setOffer(null)
    } finally {
      if (seq === pickSeq.current) setReading(false)
    }
  }

  function link(): void {
    if (couple === null) return
    const offered = offer ?? { client: null, guestCount: null, notes: null }
    save.mutate(
      {
        opportunities: couple.opportunities.slice(0, GHL_MAX_LINKS_PER_EVENT).map((o) => ({
          connectionId: o.connectionId,
          opportunityId: o.opportunityId,
          contactId: o.contactId,
          email: couple.email,
        })),
        fill: {
          client: fill.client && offered.client !== null,
          guestCount: fill.guestCount && offered.guestCount !== null,
          notes: fill.notes && offered.notes !== null,
        },
      },
      {
        onSuccess: (reply) => {
          for (const warning of reply.warnings) toast(warning, { variant: 'warning' })
          close(false)
        },
      },
    )
  }

  const offers = offer !== null && (offer.client !== null || offer.guestCount !== null || offer.notes !== null)

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Link to a GoHighLevel lead</DialogTitle>
          <DialogDescription>Choose the couple this event is for. Their leads stop being offered for new events.</DialogDescription>
        </DialogHeader>

        <GhlLeadPicker
          venueId={event.venueId}
          value={couple}
          onPick={(chosen) => void pick(chosen)}
          onClear={reset}
          idPrefix="ghl-link"
          canChooseStages={setsUpIntegrations(user)}
        />

        {reading ? (
          <p className="m-0 flex items-center gap-2 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Reading the lead from GoHighLevel…
          </p>
        ) : null}

        {offers ? (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-ink">Also fill in what this event is missing</legend>
            {offer.client !== null ? (
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  className={CHECKBOX}
                  checked={fill.client}
                  onChange={(e) => setFill({ ...fill, client: e.target.checked })}
                />
                <span>
                  Client: {offer.client.name} <span className="text-ink-muted">{offer.client.email}</span>
                </span>
              </label>
            ) : null}
            {offer.guestCount !== null ? (
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  className={CHECKBOX}
                  checked={fill.guestCount}
                  onChange={(e) => setFill({ ...fill, guestCount: e.target.checked })}
                />
                <span>Guest count: {String(offer.guestCount)}</span>
              </label>
            ) : null}
            {offer.notes !== null ? (
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  className={CHECKBOX}
                  checked={fill.notes}
                  onChange={(e) => setFill({ ...fill, notes: e.target.checked })}
                />
                <span>
                  Notes
                  <span className="block text-xs text-ink-muted">{offer.notes.split('\n').slice(0, 2).join(' · ')}</span>
                </span>
              </label>
            ) : null}
          </fieldset>
        ) : null}

        {save.error ? <FormError>{requestErrorMessage(save.error)}</FormError> : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button pending={save.isPending} type="button" disabled={couple === null || reading} onClick={link}>
            Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
